import { KNOWN_OAUTH_SCOPES } from 'src/constants/oauth/constants'
import type { OAuthScope } from 'src/constants/oauth/enums'
import type { OAuthClient } from 'src/interfaces/oauth'

/**
 * Normalises an admin OAuth client response into `OAuthClient`, or `null` when the body is not
 * a client object. Mirrors `parseGigsafeConfigResponse`.
 *
 * Two backend facts drive the normalising: the `scopes` column is nullable, so a real response
 * can carry `scopes: null`; and `deactivatedAt` is absent (rather than null) on the create
 * response, which exposes only `clientId`, `clientSecret` and `scopes`.
 *
 * Unrecognised scope strings are kept rather than filtered. Dropping them here would make
 * `resolveSubmittedScopes` unable to preserve them, turning any Save into a silent revocation.
 */
export function parseOAuthClientResponse(raw: unknown): OAuthClient | null {
  if (raw === null || raw === undefined || typeof raw !== 'object' || Array.isArray(raw)) {
    return null
  }

  const response = raw as { clientId?: unknown; scopes?: unknown; deactivatedAt?: unknown }

  if (typeof response.clientId !== 'string' || response.clientId === '') {
    return null
  }

  const scopes = Array.isArray(response.scopes)
    ? (response.scopes.filter((scope) => {
        return typeof scope === 'string'
      }) as OAuthScope[])
    : []

  const deactivatedAt =
    typeof response.deactivatedAt === 'string' && response.deactivatedAt !== ''
      ? response.deactivatedAt
      : null

  return {
    clientId: response.clientId,
    scopes,
    deactivatedAt,
  }
}

/**
 * The single place "a `deactivatedAt` timestamp means API access is off" is encoded. Not a
 * selector, so it stays testable without a store and cannot be mistaken for a memoized value.
 */
export function isOAuthClientActive(client: OAuthClient | null): boolean {
  if (client === null) {
    return false
  }

  return client.deactivatedAt === null
}

/** Returns a new array — never mutates the Formik value in place. */
export function toggleScope(
  scopes: OAuthScope[],
  scope: OAuthScope,
  isChecked: boolean,
): OAuthScope[] {
  if (!isChecked) {
    return scopes.filter((current) => {
      return current !== scope
    })
  }

  if (scopes.includes(scope)) {
    return scopes
  }

  return [...scopes, scope]
}

/**
 * The scopes to send on a save: everything the admin checked, plus any scope the server already
 * held that this UI renders no checkbox for.
 *
 * `PUT` replaces the whole `scopes` array. Without this, a backend that ever returns a scope
 * outside `OAUTH_SCOPE_OPTIONS` would have that grant silently revoked by an unrelated Save —
 * a live permission removed that the admin never saw and never intended to touch. The scope
 * enum is frozen and deprecated, so this is cheap insurance rather than an expected path.
 */
export function resolveSubmittedScopes(
  checked: OAuthScope[],
  persisted: OAuthScope[],
): OAuthScope[] {
  const preserved = persisted.filter((scope) => {
    return !KNOWN_OAUTH_SCOPES.includes(scope) && !checked.includes(scope)
  })

  return [...checked, ...preserved]
}
